// src/pages/masterdata/project/ProjectForm.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, RotateCcw, AlertCircle, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import projectService, { calculateTargetEndDate } from '../../../services/projectService';
import spkService from '../../../services/spkService';

const ProjectForm = ({ onBack, projectData = null, mode = 'add' }) => {
  // Form State
  const [formData, setFormData] = useState({
    customerId: projectData?.customerId || '',
    spkId: projectData?.spks?.[0]?.id || '',
    projectCode: projectData?.projectCode || '',
    projectName: projectData?.projectName || '',
    projectType: projectData?.projectType || '',
    startDate: projectData?.startDate || new Date().toISOString().split('T')[0],
    targetEndDate: projectData?.targetEndDate || '',
    description: projectData?.description || '',
    notes: projectData?.notes || ''
  });

  // SPK Data
  const [availableSPKs, setAvailableSPKs] = useState([]);
  const [selectedSPK, setSelectedSPK] = useState(null);
  const [inheritedItems, setInheritedItems] = useState([]);
  const [isLoadingSPKs, setIsLoadingSPKs] = useState(false);

  // Validation & Submit State
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch available SPKs (status ACCEPTED only)
  useEffect(() => {
    if (mode === 'add') {
      fetchAvailableSPKs();
    }
  }, [mode]);

  // Auto-fill when SPK is selected - FETCH DETAIL
  useEffect(() => {
    if (formData.spkId && availableSPKs.length > 0) {
      const spk = availableSPKs.find(s => s.id === parseInt(formData.spkId));
      if (spk) {
        // Fetch SPK detail to get sites/segments
        fetchSPKDetail(spk.id);
      }
    } else {
      setSelectedSPK(null);
      setInheritedItems([]);
    }
  }, [formData.spkId, availableSPKs]);

  // Fetch SPK detail with sites/segments
  const fetchSPKDetail = async (spkId) => {
    try {
      const spkDetail = await spkService.getSPKById(spkId);
      
      setSelectedSPK(spkDetail);
      
      // Auto-fill project name and type
      setFormData(prev => ({
        ...prev,
        projectName: spkDetail.projectName,
        projectType: spkDetail.projectType,
        customerId: spkDetail.customerId,
        targetEndDate: calculateTargetEndDate(spkDetail.spkDate, spkDetail.deliveryPeriod)
      }));
      
      // Inherit sites/segments from SPK
      if (spkDetail.projectType === 'TOWER') {
        setInheritedItems(spkDetail.towerSites || []);
      } else {
        setInheritedItems(spkDetail.foSegments || []);
      }
      
      toast.success(`Loaded ${spkDetail.projectType === 'TOWER' ? spkDetail.towerSiteCount : spkDetail.foSegmentCount} items from SPK`);
    } catch (error) {
      console.error('Error fetching SPK detail:', error);
      toast.error('Failed to load SPK details');
    }
  };

  const fetchAvailableSPKs = async () => {
    setIsLoadingSPKs(true);
    try {
      // Get SPKs with ACCEPTED status
      const response = await spkService.getSPKsByStatus('ACCEPTED', 0, 100);
      setAvailableSPKs(response.content || []);
    } catch (error) {
      console.error('Error fetching SPKs:', error);
      toast.error('Failed to load available SPKs');
    } finally {
      setIsLoadingSPKs(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (mode === 'add') {
      if (!formData.spkId) {
        newErrors.spkId = 'SPK is required';
      }
    }
    
    if (!formData.projectCode.trim()) {
      newErrors.projectCode = 'Project Code is required';
    } else if (!/^[A-Z0-9-]+$/.test(formData.projectCode)) {
      newErrors.projectCode = 'Project code must contain only uppercase letters, numbers, and hyphens';
    }
    
    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project Name is required';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start Date is required';
    }
    
    if (!formData.targetEndDate) {
      newErrors.targetEndDate = 'Target End Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (mode === 'add') {
        // Create project payload
        const createPayload = {
          customerId: formData.customerId,
          spkId: parseInt(formData.spkId),
          projectCode: formData.projectCode,
          projectName: formData.projectName,
          projectType: formData.projectType,
          startDate: formData.startDate,
          targetEndDate: formData.targetEndDate,
          description: formData.description || null,
          notes: formData.notes || null,
          // Include all tower site IDs or FO segment IDs from selected SPK
          towerSiteIds: formData.projectType === 'TOWER' 
            ? inheritedItems.map(item => item.id) 
            : null,
          foSegmentIds: formData.projectType === 'FO' 
            ? inheritedItems.map(item => item.id) 
            : null
        };
        
        await projectService.createProject(createPayload);
        toast.success('Project created successfully!');
      } else {
        // Update project payload
        const updatePayload = {
          projectName: formData.projectName,
          status: formData.status,
          targetEndDate: formData.targetEndDate,
          actualEndDate: formData.actualEndDate || null,
          description: formData.description || null,
          notes: formData.notes || null
        };
        
        await projectService.updateProject(projectData.id, updatePayload);
        toast.success('Project updated successfully!');
      }
      
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Error already handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (mode === 'add') {
      setFormData({
        customerId: '',
        spkId: '',
        projectCode: '',
        projectName: '',
        projectType: '',
        startDate: new Date().toISOString().split('T')[0],
        targetEndDate: '',
        description: '',
        notes: ''
      });
      setSelectedSPK(null);
      setInheritedItems([]);
    }
    setErrors({});
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to list"
              type="button"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'add' ? 'Create New Project' : 'Edit Project'}
              </h1>
              <p className="text-gray-600 mt-1">
                {mode === 'add' 
                  ? 'Select an SPK to automatically populate project details' 
                  : 'Update project information'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            disabled={isSubmitting}
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <form onSubmit={handleSubmit}>
          {/* Form Content */}
          <div className="p-6">
            <div className="space-y-6">
              {/* SPK Selection (Add Mode Only) */}
              {mode === 'add' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Select SPK <span className="text-red-600">*</span>
                    </label>
                    <select
                      name="spkId"
                      value={formData.spkId}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.spkId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      disabled={isSubmitting || isLoadingSPKs}
                    >
                      <option value="">-- Select SPK --</option>
                      {availableSPKs.map(spk => (
                        <option key={spk.id} value={spk.id}>
                          {spk.spkNumber} - {spk.customerName} ({spk.projectType}) - {spk.totalItems || 0} items
                        </option>
                      ))}
                    </select>
                    {errors.spkId && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.spkId}
                      </p>
                    )}
                    {!formData.spkId && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-800">
                          Select an SPK to automatically populate the project name, type, and inherit all sites/segments from the SPK.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Auto-filled info message */}
                  {selectedSPK && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-green-900">SPK Data Loaded</h4>
                          <p className="text-sm text-green-800 mt-1">
                            Project name, type, and {selectedSPK.projectType === 'TOWER' ? `${selectedSPK.towerSiteCount || 0} tower sites` : `${selectedSPK.foSegmentCount || 0} FO segments`} have been automatically inherited from SPK {selectedSPK.spkNumber}.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Project Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Project Code */}
                <div>
                  <label htmlFor="projectCode" className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Code <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="projectCode"
                    name="projectCode"
                    value={formData.projectCode}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      errors.projectCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., TSEL-JKT-2025-001"
                    disabled={isSubmitting || mode === 'edit'}
                  />
                  {errors.projectCode && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <span>⚠</span> {errors.projectCode}
                    </p>
                  )}
                </div>

                {/* Project Type (Auto-filled, Read-only) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Type <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.projectType === 'TOWER' ? 'Tower' : formData.projectType === 'FO' ? 'Fiber Optic' : ''}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                      placeholder="Will be set from SPK"
                    />
                    {selectedSPK && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-300">
                          From SPK
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Automatically set based on selected SPK
                  </p>
                </div>

                {/* Project Name (Auto-filled) */}
                <div className="md:col-span-2">
                  <label htmlFor="projectName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Name <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="projectName"
                      name="projectName"
                      value={formData.projectName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.projectName ? 'border-red-500 bg-red-50' : selectedSPK ? 'border-green-300 bg-green-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Telkomsel Jakarta Tower Project"
                      disabled={isSubmitting}
                    />
                    {selectedSPK && mode === 'add' && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <span className="text-xs text-green-700 bg-white px-2 py-1 rounded border border-green-300">
                          From SPK
                        </span>
                      </div>
                    )}
                  </div>
                  {errors.projectName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <span>⚠</span> {errors.projectName}
                    </p>
                  )}
                  {selectedSPK && mode === 'add' && (
                    <p className="mt-1 text-xs text-green-700">
                      Inherited from SPK. You can modify if needed.
                    </p>
                  )}
                </div>

                {/* Start Date */}
                <div>
                  <label htmlFor="startDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    Start Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      errors.startDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting || mode === 'edit'}
                  />
                  {errors.startDate && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <span>⚠</span> {errors.startDate}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Default: Today's date
                  </p>
                </div>

                {/* Target End Date */}
                <div>
                  <label htmlFor="targetEndDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    Target End Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    id="targetEndDate"
                    name="targetEndDate"
                    value={formData.targetEndDate}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      errors.targetEndDate ? 'border-red-500 bg-red-50' : selectedSPK ? 'border-green-300 bg-green-50' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.targetEndDate && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <span>⚠</span> {errors.targetEndDate}
                    </p>
                  )}
                  {selectedSPK && mode === 'add' && (
                    <p className="mt-1 text-xs text-green-700">
                      Auto-calculated: SPK Date + {selectedSPK.deliveryPeriod} months delivery period
                    </p>
                  )}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                    placeholder="Optional description about the project"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                    placeholder="Additional notes"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Inherited Items Summary */}
              {selectedSPK && inheritedItems.length > 0 && (
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Inherited {formData.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments'} ({inheritedItems.length})
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {inheritedItems.map((item, idx) => (
                      <div key={item.id} className="bg-white p-3 rounded-lg border border-blue-200 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold ${
                            formData.projectType === 'TOWER' ? 'bg-purple-600' : 'bg-green-600'
                          }`}>
                            {idx + 1}
                          </div>
                          <div>
                            {formData.projectType === 'TOWER' ? (
                              <>
                                <div className="text-sm font-semibold text-gray-900">{item.siteName}</div>
                                <div className="text-xs text-gray-600">{item.siteId} • {item.location}</div>
                              </>
                            ) : (
                              <>
                                <div className="text-sm font-semibold text-gray-900">{item.segmentName}</div>
                                <div className="text-xs text-gray-600">{item.spanId} • {item.kota}</div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {formData.projectType === 'TOWER' ? (
                            <span className="text-sm font-bold text-gray-900">{item.towerHeight} m</span>
                          ) : (
                            <span className="text-sm font-bold text-gray-900">{(item.length / 1000).toFixed(2)} km</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-800 mt-3 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    These items are inherited from the SPK and will be automatically included in this project.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-blue-600 via-red-600 to-blue-600"></div>

          {/* Action Buttons */}
          <div className="p-6 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onBack}
                className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <Save className="w-5 h-5" />
                {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Create Project' : 'Update Project')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;